/* ============================================================
   Puddi Cart Drawer — section JS
   Ported from puddi_cart_drawer_fidelity_v1.html with real Shopify AJAX Cart API.

   Endpoints (from window.PUDDI_CART.routes):
     GET  /cart.js         → current cart state
     POST /cart/change.js  → update line qty / remove (by line index or key)
     POST /cart/add.js     → add item

   Emits/listens on window: 'cart:updated' custom event. Any surface that adds
   to cart (BYB, PDP form intercept, future upsells) fires this; drawer, header
   badge, and any other listener re-sync from a single fetch.

   Exposes:
     window.PuddiCart.open()      — open drawer, refresh cart
     window.PuddiCart.close()     — close drawer
     window.PuddiCart.refresh()   — fetch cart + re-render (no open)
   ============================================================ */
(function(){
  'use strict';

  if(!window.PUDDI_CART) return;
  const CFG = window.PUDDI_CART;
  const drawer = document.querySelector('[data-puddi-cart-drawer]');
  if(!drawer) return;

  // ============ trust pool + empty pool ============
  const TRUST = [
    "Free shipping in the USA",
    "100% Money-Back Guarantee",
    "Ships in 24 hours",
    "Shelf-stable · no fridge needed",
    "Made in the USA",
    "Order today, spoon tomorrow"
  ];

  const EMPTY_POOL = [
    "Nothing in here yet.<br>The pudding is patient.",
    "An empty cart.<br>So much potential.",
    "All this room.<br>Imagine the pudding."
  ];

  // ============ formatters ============
  function money(cents){ return '$' + (cents/100).toFixed(2); }
  function threshLine(subtotal, threshold){
    if(subtotal >= threshold) return "Free shipping unlocked. <em>The box rides for free.</em>";
    return `You're ${money(threshold - subtotal)} from free shipping.`;
  }
  function trustHTML(label){ return `<span class="pcd-trust-item"><span class="pcd-tk">✓</span>${label}</span>`; }

  // ============ state ============
  let cart = null;
  let fetching = false;
  let opStack = 0; // pending write ops — prevents render race on rapid clicks

  // ============ shufflers ============
  function shuffleTrust(){
    const picks = window.PuddiShuffle.shuffle(TRUST).slice(0, 2);
    drawer.querySelectorAll('[data-zone="foot-trust"]').forEach(el=>{
      el.innerHTML = picks.map(trustHTML).join('');
    });
  }
  function rotateEmpty(){
    const line = window.PuddiShuffle.shuffle(EMPTY_POOL)[0];
    drawer.querySelectorAll('[data-zone="empty-line"]').forEach(el=>{ el.innerHTML = line; });
  }

  // ============ line item thumb — from cart line data ============
  function thumbForLine(line){
    // BYB parent detection: product handle match OR presence of _composition_data property
    const props = line.properties || {};
    const isBYB = line.handle === 'build-your-pudding-box' || props._composition_data;
    if(isBYB) return `<div class="pcd-li-thumb t-byb"><i></i><i></i><i></i><i></i></div>`;

    // Regular product — handle-based thumb URL lookup (guaranteed from PUDDI_CART.thumb_urls),
    // fall back to line.featured_image if any surface adds a SKU we haven't mapped, then flat color.
    const handle = line.handle || '';
    const isVan = handle.indexOf('vanilla') !== -1;
    let thumbUrl = (CFG.thumb_urls && CFG.thumb_urls[handle]) || '';
    if(!thumbUrl && line.featured_image && line.featured_image.url) thumbUrl = line.featured_image.url;
    const bgImage = thumbUrl ? `background-image:url('${thumbUrl}')` : '';
    return `<div class="pcd-li-thumb ${isVan?'t-van':''}" style="${bgImage}"></div>`;
  }

  // ============ line item variant/composition line ============
  function variantLine(line){
    const props = line.properties || {};
    // BYB parent: use the Composition property, not variant title
    if(props.Composition) return props.Composition;
    if(props._composition_data) {
      try {
        const d = JSON.parse(props._composition_data);
        const bits = [];
        if(d.cr) bits.push(`${d.cr}× Choco`);
        if(d.cn) bits.push(`${d.cn}× Choco NSA`);
        if(d.vr) bits.push(`${d.vr}× Van`);
        if(d.vn) bits.push(`${d.vn}× Van NSA`);
        return bits.join(' · ');
      } catch(e){ /* fall through */ }
    }
    // Regular product: variant title (e.g. "6-Pack") + selling plan name if any
    let s = line.variant_title || '';
    if(line.selling_plan_allocation && line.selling_plan_allocation.selling_plan){
      const plan = line.selling_plan_allocation.selling_plan.name || 'Subscribe & save';
      s = s ? `${s} · ${plan}` : plan;
    } else if(s) {
      s = `${s} · One-time`;
    }
    return s;
  }

  // ============ line item NSA chip ============
  function isNSA(line){
    // Product-level metafield would be cleanest; heuristic on handle for now
    const handle = line.handle || '';
    const props = line.properties || {};
    if(props._composition_data) return false; // BYB parent shows composition, not NSA
    return handle.indexOf('no-sugar-added') !== -1;
  }

  // ============ render ============
  function render(){
    if(!cart) return;

    const isEmpty = cart.item_count === 0;
    const subtotal = cart.total_price; // cents

    // Update global cart count on header + any listeners
    document.querySelectorAll('[data-cart-count]').forEach(el=>{ el.textContent = cart.item_count; });

    // Head count
    drawer.querySelectorAll('[data-zone="count"]').forEach(el=>{ el.textContent = `(${cart.item_count})`; });

    // Toggle empty vs cart body
    drawer.querySelector('[data-zone="thresh"]').hidden = isEmpty;
    drawer.querySelector('[data-zone="items"]').hidden = isEmpty;
    drawer.querySelector('[data-zone="foot"]').hidden = isEmpty;
    drawer.querySelector('[data-zone="empty"]').hidden = !isEmpty;

    if(isEmpty){
      shuffleTrust(); // for next open — no-op cost
      return;
    }

    // Threshold
    const threshold = CFG.free_ship_threshold_cents;
    drawer.querySelector('[data-zone="thresh-line"]').innerHTML = threshLine(subtotal, threshold);
    const fill = drawer.querySelector('[data-zone="thresh-fill"]');
    fill.style.width = Math.min(100, subtotal/threshold*100) + '%';
    fill.classList.toggle('gold', subtotal >= threshold);

    // Subtotal
    drawer.querySelector('[data-zone="subtotal"]').textContent = money(subtotal);

    // Line items
    const zone = drawer.querySelector('[data-zone="items"]');
    zone.innerHTML = '';
    cart.items.forEach((line, idx)=>{
      const row = document.createElement('div');
      row.className = 'pcd-line-item';
      row.dataset.key = line.key;
      row.dataset.index = idx + 1; // /cart/change.js uses 1-based line indexes

      const compareAt = line.original_line_price !== line.final_line_price ? line.original_line_price : null;
      const priceHTML = `${money(line.final_line_price)}${compareAt ? `<s>${money(compareAt)}</s>` : ''}`;

      const hasSub = !!(line.selling_plan_allocation && line.selling_plan_allocation.selling_plan);
      const subHTML = hasSub ? `<div class="pcd-li-sub">↻ ${line.selling_plan_allocation.selling_plan.name || 'Subscribe & save'}</div>` : '';

      const nsaHTML = isNSA(line) ? `<span class="pcd-li-nsa">No Sugar Added</span>` : '';

      // BYB parent lines concatenate the size (variant_title) onto the product name so
      // multiple BYB boxes in the cart are distinguishable at a glance. Composition text
      // stays in the variant line below.
      const lineProps = line.properties || {};
      const isBYBLine = line.handle === 'build-your-pudding-box' || lineProps._composition_data;
      const nameText = (isBYBLine && line.variant_title) ? `${line.product_title} · ${line.variant_title}` : line.product_title;

      row.innerHTML = `
        ${thumbForLine(line)}
        <div class="pcd-li-body">
          <div class="pcd-li-name">${nameText}${nsaHTML}</div>
          <div class="pcd-li-variant">${variantLine(line)}</div>
          ${subHTML}
          <div class="pcd-li-controls">
            <div class="pcd-li-step">
              <button type="button" class="pcd-i-minus" aria-label="Remove one">−</button>
              <span class="n">${line.quantity}</span>
              <button type="button" class="pcd-i-plus" aria-label="Add one">+</button>
            </div>
            <button type="button" class="pcd-li-remove">Remove</button>
          </div>
        </div>
        <div class="pcd-li-price">${priceHTML}</div>`;

      row.querySelector('.pcd-i-plus').addEventListener('click', ()=>changeQty(line.key, line.quantity + 1));
      row.querySelector('.pcd-i-minus').addEventListener('click', ()=>changeQty(line.key, Math.max(0, line.quantity - 1)));
      row.querySelector('.pcd-li-remove').addEventListener('click', ()=>changeQty(line.key, 0));

      zone.appendChild(row);
    });

    shuffleTrust();
  }

  // ============ cart ops ============
  function fetchCart(){
    fetching = true;
    return fetch(CFG.routes.get, { headers:{'Accept':'application/json'} })
      .then(r=>r.json())
      .then(data=>{ cart = data; render(); })
      .catch(err=>{ console.error('[Cart]', err); })
      .finally(()=>{ fetching = false; });
  }

  function changeQty(key, qty){
    opStack++;
    fetch(CFG.routes.change, {
      method: 'POST',
      headers: {'Content-Type':'application/json', 'Accept':'application/json'},
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(r=>r.json())
    .then(data=>{ cart = data; render(); window.dispatchEvent(new CustomEvent('cart:updated')); })
    .catch(err=>{ console.error('[Cart]', err); fetchCart(); })
    .finally(()=>{ opStack--; });
  }

  // ============ open / close ============
  function openDrawer(){
    drawer.setAttribute('data-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    rotateEmpty();
    shuffleTrust();
    fetchCart();
  }

  function closeDrawer(){
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // Close bindings
  drawer.querySelectorAll('[data-close]').forEach(el=>{
    el.addEventListener('click', closeDrawer);
  });
  // ESC to close
  document.addEventListener('keydown', e=>{
    if(e.key === 'Escape' && drawer.hasAttribute('data-open')) closeDrawer();
  });

  // ============ global form intercept — PDP + any future ATC forms ============
  document.addEventListener('submit', function(e){
    const form = e.target;
    if(!form || !form.action) return;
    if(form.action.indexOf('/cart/add') === -1) return;
    if(form.dataset.puddiCartSkip === 'true') return; // opt-out escape hatch

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if(submitBtn) submitBtn.disabled = true;

    // Optimistic drawer open — populates when fetch resolves
    openDrawer();

    const fd = new FormData(form);
    fetch(CFG.routes.add, {
      method: 'POST',
      headers: {'Accept':'application/json'},
      body: fd
    })
    .then(r=>{
      if(!r.ok) return r.json().then(err=>{ throw new Error(err.description || 'Add to cart failed'); });
      return r.json();
    })
    .then(()=>{
      window.dispatchEvent(new CustomEvent('cart:updated'));
    })
    .catch(err=>{
      console.error('[Cart]', err);
      alert(err.message || 'Sorry, something went wrong adding to your cart.');
    })
    .finally(()=>{
      if(submitBtn) submitBtn.disabled = false;
    });
  });

  // ============ listen for cart:updated (fired by us + BYB + any future add path) ============
  window.addEventListener('cart:updated', ()=>{
    if(!fetching && opStack === 0) fetchCart();
  });

  // ============ public API ============
  window.PuddiCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: fetchCart
  };

  // ============ header cart ring intercept ============
  // Batch 2 header shipped the cart ring as an anchor href={{ routes.cart_url }}.
  // Global click handler catches any element with data-puddi-cart-open, plus
  // the .cartring class from batch 2 as a fallback.
  document.addEventListener('click', function(e){
    const trigger = e.target.closest('[data-puddi-cart-open], .cartring');
    if(!trigger) return;
    e.preventDefault();
    openDrawer();
  });

  // ============ initial hydration — for header count badge ============
  fetchCart();

})();
